import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import {
  removeFriend as removeFriendApi,
} from '../services/api/friends';
import { getErrorMessage } from '../utils/errorUtils';


interface FriendshipContextType {
  loadingFriends: boolean;
  error: string | null;
  removeFriend: (friendId: string) => Promise<void>;
  /** Incremented when the friends list may have changed (e.g. friend removed/added). Consumers can react to this. */
  refreshVersion: number;
  /** Trigger a refresh signal for consumers that maintain their own friends list. */
  triggerRefresh: () => void;
}

// Default context value for when used outside provider (e.g., in tests)
const defaultContextValue: FriendshipContextType = {
  loadingFriends: false,
  error: null,
  removeFriend: async () => {},
  refreshVersion: 0,
  triggerRefresh: () => {},
};

const FriendshipContext = createContext<FriendshipContextType>(defaultContextValue);

export const useFriendship = (): FriendshipContextType => {
  return useContext(FriendshipContext);
};

interface FriendshipProviderProps {
  children: ReactNode;
}

export const FriendshipProvider: React.FC<FriendshipProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshVersion(v => v + 1);
  }, []);

  const removeFriend = useCallback(async (friendId: string) => {
    try {
      setError(null);
      await removeFriendApi(friendId);
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to remove friend');
      setError(message);
      throw err;
    }
  }, []);

  const value: FriendshipContextType = {
    loadingFriends: false,
    error,
    removeFriend,
    refreshVersion,
    triggerRefresh,
  };

  return (
    <FriendshipContext.Provider value={value}>
      {children}
    </FriendshipContext.Provider>
  );
};

export default FriendshipContext;
